import { useRef, useState } from 'react';
import { Clock, Waves, Info } from 'lucide-react';
import { POOL_SCHEDULE, POOL_FACTS } from '../data/swimLevels';

/**
 * Compact, mobile-friendly pool-hours table — 7 day tabs (or accordions on mobile).
 * Reflects the 09/03/2026 updated pool schedule.
 */
const PoolHoursTable = () => {
  const [activeDay, setActiveDay] = useState(POOL_SCHEDULE[0].day);
  const day = POOL_SCHEDULE.find((d) => d.day === activeDay);
  const tabRefs = useRef([]);
  const onTabKeyDown = (event, index) => {
    const count = POOL_SCHEDULE.length;
    const next = event.key === 'ArrowRight' ? (index + 1) % count
      : event.key === 'ArrowLeft' ? (index + count - 1) % count
      : event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : null;
    if (next === null) return;
    event.preventDefault();
    setActiveDay(POOL_SCHEDULE[next].day);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="space-y-6" data-testid="pool-hours-table">
      {/* Two-pool callout */}
      <div className="grid sm:grid-cols-2 gap-4">
        {POOL_FACTS.pools.map((p) => (
          <div key={p.name} className="dv-family__panel p-5" data-testid={`pool-card-${p.name.toLowerCase().replace(/\W+/g,'-')}`}>
            <div className="flex items-center gap-2 mb-1">
              <Waves size={18} className="text-[#0072BB]" />
              <h3 className="dv-size-subheading font-bold text-lg text-gray-900">{p.name}</h3>
            </div>
            <div className="dv-size-subheading text-2xl font-black text-[#0072BB] mb-1">{p.length} · {p.temp}</div>
            <p className="dv-size-body text-sm text-gray-600">{p.use}</p>
          </div>
        ))}
      </div>

      {/* Day tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-gray-200 pb-1" role="tablist" aria-label="Pool schedule day">
        {POOL_SCHEDULE.map((d, index) => {
          const active = d.day === activeDay;
          return (
            <button
              key={d.day}
              type="button"
              role="tab"
              id={`pool-tab-${d.day.toLowerCase()}`}
              aria-controls="pool-day-panel"
              tabIndex={active ? 0 : -1}
              ref={(node) => { tabRefs.current[index] = node; }}
              onKeyDown={(event) => onTabKeyDown(event, index)}
              aria-selected={active}
              onClick={() => setActiveDay(d.day)}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
                active ? 'bg-[#0072BB] text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`pool-day-tab-${d.day.toLowerCase()}`}
            >
              {d.day.slice(0, 3)}
            </button>
          );
        })}
      </div>

      {/* Activity rows for selected day */}
      <div id="pool-day-panel" role="tabpanel" tabIndex={0} aria-labelledby={`pool-tab-${activeDay.toLowerCase()}`} className="space-y-2" data-testid={`pool-day-content-${activeDay.toLowerCase()}`}>
        {day && day.activities.map((a, i) => {
          const isClosed = a.pool === '—';
          return (
            <div
              key={i}
              className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 rounded-lg border ${
                isClosed
                  ? 'bg-gray-50 border-gray-200 text-gray-500'
                  : a.pool === 'Lap Pool'
                    ? 'bg-blue-50/50 border-blue-100'
                    : 'bg-cyan-50/50 border-cyan-100'
              }`}
            >
              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                <div className={`shrink-0 w-1 h-10 rounded-full ${
                  a.pool === 'Lap Pool' ? 'bg-[#0072BB]' : a.pool === 'Lesson Pool' ? 'bg-cyan-500' : 'bg-gray-300'
                }`} />
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 text-sm md:text-base">{a.name}</div>
                  <div className="text-xs text-gray-500">{a.pool}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:text-right">
                <Clock size={14} className="text-gray-400 sm:hidden" />
                <div>
                  <div className={`font-bold ${isClosed ? 'text-gray-500' : 'text-gray-900'}`}>{a.time}</div>
                  {a.note && <div className="text-xs text-gray-500">{a.note}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 text-xs text-gray-500 mt-4">
        <Info size={14} className="text-gray-400 shrink-0 mt-0.5" />
        <p>
          Schedule updated 09/03/2026. The Lesson Pool (15 yards · 93°F) is used for swim lessons, physical therapy
          and scheduled beach parties. Some advanced lessons, Family Swim and Lap Swim use the Lap Pool (25 yards · 83°F). All hours apply to the Brentwood location.
        </p>
      </div>
      <p className="dv-size-body dv-family__notice">
        <strong>Family Swim has an additional charge</strong> on top of membership. Stop by the front desk or call{' '}
        <a href="tel:+19252402990">(925) 240-2990</a> for current Family Swim pricing.
      </p>
    </div>
  );
};

export default PoolHoursTable;
